import { useEffect, useMemo, useRef, useState, type ComponentRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { YoutubeVideoDto } from '../../api/types.js';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import styles from './YouTube.module.css';

declare global {
  interface Window {
    YT?: {
      Player: new (
        element: HTMLElement,
        options: {
          videoId: string;
          host?: string;
          playerVars?: Record<string, string | number>;
          events?: {
            onReady?: (event: { target: YoutubePlayerHandle }) => void;
            onStateChange?: (event: { data: number }) => void;
          };
        },
      ) => YoutubePlayerHandle;
      PlayerState?: { ENDED: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YoutubePlayerHandle {
  playVideo(): void;
  destroy(): void;
}

const PLAYED_KEY = (classroomId: string) => `classy:youtubePlayed:${classroomId}`;
const YOUTUBE_ENDED = 0;

let apiPromise: Promise<void> | null = null;

function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return apiPromise;
}

export function YouTube() {
  const api = useApi();
  const { active } = useActiveClassroom();
  const [playedIds, setPlayedIds] = useState<Set<string>>(() => new Set());
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [playAll, setPlayAll] = useState(false);
  const [autoplay, setAutoplay] = useState(false);

  const videosQuery = useQuery({
    queryKey: ['youtube-videos', active?.id ?? 'none'],
    queryFn: () => api.youtube.listVideos(active!.id),
    enabled: active !== null,
  });

  const videos = useMemo(() => videosQuery.data ?? [], [videosQuery.data]);
  const activeVideo = videos.find((v) => v.id === activeVideoId) ?? null;
  const unplayed = videos.filter((v) => !playedIds.has(v.videoId));
  const played = videos.filter((v) => playedIds.has(v.videoId));
  const nextVideo = activeVideo ? findNextUnplayed(videos, playedIds, activeVideo.id) : null;

  useEffect(() => {
    if (!active) return;
    setPlayedIds(readPlayed(active.id));
  }, [active?.id, active]);

  useEffect(() => {
    if (!active) return;
    const currentVideoIds = new Set(videos.map((v) => v.videoId));
    setPlayedIds((prev) => {
      const next = new Set([...prev].filter((id) => currentVideoIds.has(id)));
      writePlayed(active.id, next);
      return next;
    });
  }, [active, videos]);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  function persistPlayed(next: Set<string>) {
    if (!active) return;
    setPlayedIds(next);
    writePlayed(active.id, next);
  }

  function play(video: YoutubeVideoDto, all = false) {
    setPlayAll(all);
    setAutoplay(all);
    setActiveVideoId(video.id);
  }

  function startVideo(video: YoutubeVideoDto) {
    markPlayed(video);
  }

  function resetPlaylist() {
    if (!active) return;
    setActiveVideoId(null);
    setPlayAll(false);
    persistPlayed(new Set());
  }

  function markPlayed(video: YoutubeVideoDto) {
    const next = new Set(playedIds);
    next.add(video.videoId);
    persistPlayed(next);
    return next;
  }

  function onEnded() {
    if (!activeVideo) return;
    const remaining = videos.filter((v) => !playedIds.has(v.videoId) && v.id !== activeVideo.id);
    if (playAll && remaining.length > 0) {
      setAutoplay(true);
      setActiveVideoId(remaining[0]!.id);
    } else {
      setActiveVideoId(null);
      setPlayAll(false);
    }
  }

  function goNext() {
    if (!activeVideo) return;
    const nextTarget = nextVideo;
    markPlayed(activeVideo);
    if (nextTarget) {
      setAutoplay(true);
      setActiveVideoId(nextTarget.id);
    } else {
      setActiveVideoId(null);
      setPlayAll(false);
    }
  }

  return (
    <section className={styles.youtube} data-testid="youtube-page">
      <div className={styles.shell}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>YouTube Playlist</h1>
            <p className={styles.subtitle}>
              {unplayed.length} ready · {played.length} played
            </p>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.actionButton}
              disabled={unplayed.length === 0}
              onClick={() => {
                if (unplayed[0]) play(unplayed[0], true);
              }}
              data-testid="youtube-play-all"
            >
              Play all
            </button>
            <button
              type="button"
              className={styles.actionButton}
              disabled={playedIds.size === 0}
              onClick={resetPlaylist}
              data-testid="youtube-reset"
            >
              Reset playlist
            </button>
          </div>
        </header>

        {videosQuery.isLoading ? <p className={styles.notice}>Loading playlist...</p> : null}

        {!videosQuery.isLoading && videos.length === 0 ? (
          <p className={styles.notice}>
            No YouTube videos have been added yet. Add videos from this classroom&rsquo;s YouTube
            settings tab.
          </p>
        ) : null}

        {unplayed.length > 0 ? (
          <div className={styles.grid} data-testid="youtube-unplayed-grid">
            {unplayed.map((video) => (
              <VideoCard key={video.id} video={video} onPlay={() => play(video)} />
            ))}
          </div>
        ) : null}

        {videos.length > 0 && unplayed.length === 0 ? (
          <p className={styles.notice}>
            All videos have been played. Reset the playlist to show them again.
          </p>
        ) : null}

        {played.length > 0 ? (
          <details className={styles.played} data-testid="youtube-played-list">
            <summary>Played videos</summary>
            <div className={styles.playedList}>
              {played.map((video) => (
                <button
                  key={video.id}
                  type="button"
                  className={styles.playedButton}
                  onClick={() => play(video)}
                >
                  <img className={styles.playedThumb} src={thumbFor(video)} alt="" />
                  <span className={styles.playedText}>
                    <strong>{video.title}</strong>
                    <span>{video.channelTitle || 'YouTube'}</span>
                  </span>
                </button>
              ))}
            </div>
          </details>
        ) : null}
      </div>

      {activeVideo ? (
        <PlayerOverlay
          key={activeVideo.id}
          video={activeVideo}
          autoplay={autoplay}
          hasNext={nextVideo !== null}
          onBack={() => {
            setActiveVideoId(null);
            setPlayAll(false);
          }}
          onEnded={onEnded}
          onNext={goNext}
          onStart={() => startVideo(activeVideo)}
        />
      ) : null}
    </section>
  );
}

function VideoCard({ video, onPlay }: { video: YoutubeVideoDto; onPlay: () => void }) {
  const thumbnailUrl = thumbFor(video);
  return (
    <article className={styles.card} data-testid="youtube-video-card">
      <button type="button" className={styles.thumbButton} onClick={onPlay}>
        <img className={styles.thumb} src={thumbnailUrl} alt="" />
        <span className={styles.playBadge}>Play</span>
      </button>
      <div className={styles.cardText}>
        <h2>{video.title}</h2>
        <p>{video.channelTitle || 'YouTube'}</p>
      </div>
    </article>
  );
}

function PlayerOverlay({
  video,
  autoplay,
  hasNext,
  onBack,
  onEnded,
  onNext,
  onStart,
}: {
  video: YoutubeVideoDto;
  autoplay: boolean;
  hasNext: boolean;
  onBack: () => void;
  onEnded: () => void;
  onNext: () => void;
  onStart: () => void;
}) {
  const iframeRef = useRef<ComponentRef<'iframe'> | null>(null);
  const onEndedRef = useRef(onEnded);
  const onStartRef = useRef(onStart);
  const startedVideoRef = useRef<string | null>(null);
  const [started, setStarted] = useState(autoplay);
  const [shouldPlay, setShouldPlay] = useState(autoplay);
  const thumbnailUrl = thumbFor(video);
  const playerSrc = useMemo(() => {
    const params = new URLSearchParams({
      autoplay: shouldPlay ? '1' : '0',
      controls: '1',
      enablejsapi: '1',
      playsinline: '1',
      rel: '0',
      origin: window.location.origin,
    });
    return `https://www.youtube-nocookie.com/embed/${video.videoId}?${params.toString()}`;
  }, [shouldPlay, video.videoId]);

  useEffect(() => {
    onEndedRef.current = onEnded;
    onStartRef.current = onStart;
  }, [onEnded, onStart]);

  function notifyStarted() {
    if (startedVideoRef.current === video.id) return;
    startedVideoRef.current = video.id;
    onStartRef.current();
  }

  useEffect(() => {
    if (!started) return;
    let cancelled = false;
    let player: YoutubePlayerHandle | null = null;
    void loadYouTubeApi().then(() => {
      if (cancelled || !iframeRef.current || !window.YT?.Player) return;
      player = new window.YT.Player(iframeRef.current, {
        videoId: video.videoId,
        events: {
          onReady: (event) => {
            notifyStarted();
            if (shouldPlay) event.target.playVideo();
          },
          onStateChange: (event) => {
            if (event.data === (window.YT?.PlayerState?.ENDED ?? YOUTUBE_ENDED)) {
              onEndedRef.current();
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      player?.destroy();
    };
  }, [shouldPlay, started, video.id, video.videoId]);

  useEffect(() => {
    setStarted(autoplay);
    setShouldPlay(autoplay);
    startedVideoRef.current = null;
  }, [autoplay, video.id]);

  return (
    <div className={styles.overlay} data-testid="youtube-player-overlay">
      <header className={styles.playerHeader}>
        <button type="button" className={styles.playerButton} onClick={onBack}>
          Back
        </button>
        <div className={styles.playerTitle}>
          <strong>{video.title}</strong>
          <span>{video.channelTitle || 'YouTube'}</span>
        </div>
        <button type="button" className={styles.playerButton} onClick={onNext} disabled={!hasNext}>
          Next
        </button>
      </header>
      <div className={`${styles.playerFrame} ${started ? '' : styles.posterFrame}`}>
        {started ? (
          <iframe
            ref={iframeRef}
            className={styles.iframeMount}
            src={playerSrc}
            title={video.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className={styles.posterButton}
            onClick={() => {
              notifyStarted();
              setShouldPlay(true);
              setStarted(true);
            }}
            data-testid="youtube-poster-play"
          >
            <span className={styles.posterMedia}>
              <img className={styles.posterImage} src={thumbnailUrl} alt="" />
              <span className={styles.posterShade} aria-hidden="true" />
              <span className={styles.posterPlay}>Play</span>
            </span>
            <span className={styles.posterText}>
              <strong>{video.title}</strong>
              <span>{video.channelTitle || 'YouTube'}</span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

function thumbFor(video: YoutubeVideoDto): string {
  return video.thumbnailUrl || `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`;
}

function findNextUnplayed(
  videos: YoutubeVideoDto[],
  playedIds: Set<string>,
  currentId: string,
): YoutubeVideoDto | null {
  const currentIndex = videos.findIndex((video) => video.id === currentId);
  if (currentIndex === -1) return null;
  const ordered = [...videos.slice(currentIndex + 1), ...videos.slice(0, currentIndex)];
  return ordered.find((video) => video.id !== currentId && !playedIds.has(video.videoId)) ?? null;
}

function readPlayed(classroomId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(PLAYED_KEY(classroomId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writePlayed(classroomId: string, ids: Set<string>): void {
  try {
    sessionStorage.setItem(PLAYED_KEY(classroomId), JSON.stringify([...ids]));
  } catch {
    /* ignore quota / private mode */
  }
}
